import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCartStore }    from '../store/cartStore.js';
import { useSessionStore } from '../store/sessionStore.js';
import { useTerminalStore } from '../store/terminalStore.js';
import { useTabsStore }    from '../store/tabsStore.js';
import { createPayment }   from '../api/pos.api.js';
import paymentTerminal     from '../native/paymentTerminal.js';
import { Capacitor }      from '@capacitor/core';
import { v4 as uuidv4 }    from 'uuid';
import { IcoCash, IcoCard, IcoTransfer, IcoMixed, IcoUser, IcoWarning, IcoCheck } from '../components/common/Icons.jsx';
import styles from './CheckoutScreen.module.css';

const isNative = Capacitor.isNativePlatform();

const METHODS = [
  { key: 'cash',     label: 'Efectivo',      Icon: IcoCash     },
  { key: 'card',     label: 'Tarjeta',        Icon: IcoCard     },
  { key: 'transfer', label: 'Transferencia',  Icon: IcoTransfer },
  { key: 'mixed',    label: 'Mixto',          Icon: IcoMixed    },
];

const fmt = (n) => `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

export default function CheckoutScreen() {
  const navigate   = useNavigate();
  const session              = useSessionStore((s) => s.session);
  const updateSessionTotals  = useSessionStore((s) => s.updateSessionTotals);
  const customer   = useCartStore((s) => s.customer);
  const items      = useCartStore((s) => s.items);
  const discount   = useCartStore((s) => s.discount);
  const subtotal   = useCartStore((s) => s.getSubtotal());
  const total      = useCartStore((s) => s.getTotal());
  const note       = useCartStore((s) => s.note);
  const clearCart  = useCartStore((s) => s.clearCart);

  const terminal       = useSessionStore((s) => s.terminal);
  const terminalStatus = useTerminalStore((s) => s.status);
  const activeTabId    = useTabsStore((s) => s.activeTabId);
  const closeTab       = useTabsStore((s) => s.closeTab);
  const clearActiveTab = useTabsStore((s) => s.clearActiveTab);
  const abortRef       = useRef(null);

  const [method,         setMethod]         = useState('cash');
  const [showItems,      setShowItems]      = useState(false);
  const [amountCash,     setAmountCash]     = useState('');
  const [mixedCash,      setMixedCash]      = useState('');   // cash portion for mixed
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState(null);
  const [terminalPhase,  setTerminalPhase]  = useState(null); // null | 'preparing' | 'waiting' | 'processing'

  const overStockItems = items.filter(
    (i) => i.product.stock != null && i.quantity > i.product.stock,
  );

  const provider = terminal?.terminal_provider ?? 'stripe';
  const needsNative = provider === 'stripe';

  const changeDue = method === 'cash' && amountCash
    ? Math.max(0, Number(amountCash) - total)
    : 0;

  // mixed: card = total - cash
  const mixedCashNum  = Math.min(Math.max(0, Number(mixedCash) || 0), total);
  const mixedCardNum  = Math.max(0, total - mixedCashNum);
  const mixedValid    = method === 'mixed' && mixedCashNum > 0 && mixedCardNum > 0;

  const buildItems = () => items.map((i) => ({
    product_id: i.product._id,
    name:       i.product.name,
    quantity:   i.quantity,
    unit_price: i.unit_price,
  }));

  const buildCfdiItems = () => items.map((i) => ({
    name:            i.product.name,
    quantity:        i.quantity,
    unit_price:      i.unit_price,
    original_price:  i.product.price !== i.unit_price ? i.product.price : undefined,
    clave_prod_serv: i.product.clave_prod_serv ?? null,
    clave_unidad:    i.product.clave_unidad    ?? 'H87',
  }));

  const finishPayment = (payment, { client_uuid }) => {
    updateSessionTotals({
      sales_count:   (session.sales_count   ?? 0) + 1,
      sales_total:   (session.sales_total   ?? 0) + total,
      expected_cash: (session.expected_cash ?? 0) + (method === 'cash' ? total : 0),
    });
    if (activeTabId) { closeTab(activeTabId); clearActiveTab(); }
    clearCart();
    navigate('/receipt', {
      state: { total, subtotal, discount, method, changeDue, client_uuid, paymentId: payment?._id ?? null, cfdiItems: buildCfdiItems(), note, customer },
      replace: true,
    });
  };

  const handleCharge = async () => {
    setError(null);

    // ── Tarjeta / Mixto-card: flujo terminal ────────────────────────────────
    if (method === 'card' || method === 'mixed') {
      const cardAmount = method === 'mixed' ? mixedCardNum : total;

      if (needsNative && !isNative) {
        setError('El cobro con tarjeta Stripe requiere el dispositivo físico (iPad/Android)');
        return;
      }
      if (provider === 'stripe' && terminalStatus !== 'connected') {
        setError('El lector de tarjetas no está conectado. Conéctalo desde el menú de configuración.');
        return;
      }

      abortRef.current = new AbortController();
      setLoading(true);
      try {
        const client_uuid = uuidv4();
        const { providerPaymentId } = await paymentTerminal.charge({
          amount:      cardAmount,
          terminal,
          externalRef: client_uuid,
          signal:      abortRef.current.signal,
          onPhase:     setTerminalPhase,
        });

        const isStripe = provider === 'stripe';
        const payment = await createPayment({
          client_uuid,
          session_id:               session._id,
          method,
          total,
          discount:                 discount || 0,
          amount_cash:              method === 'mixed' ? mixedCashNum : 0,
          amount_card:              cardAmount,
          change_given:             0,
          customer_id:              customer?._id ?? null,
          stripe_payment_intent_id: isStripe ? providerPaymentId : null,
          mp_payment_id:            !isStripe ? providerPaymentId : null,
          items:                    buildItems(),
          note:                     note || null,
        });
        finishPayment(payment, { client_uuid });
      } catch (err) {
        const msg = err.message?.toLowerCase().includes('cancel')
          ? 'Cobro cancelado'
          : (err.response?.data?.error ?? err.message ?? 'Error al procesar la tarjeta');
        setError(msg);
      } finally {
        setLoading(false);
        setTerminalPhase(null);
        abortRef.current = null;
      }
      return;
    }

    // ── Efectivo / transferencia ─────────────────────────────────────────────
    setLoading(true);
    try {
      const client_uuid = uuidv4();
      const payment = await createPayment({
        client_uuid,
        session_id:      session._id,
        method,
        total,
        discount:        discount || 0,
        amount_cash:     method === 'cash'     ? total : 0,
        amount_card:     0,
        amount_transfer: method === 'transfer' ? total : 0,
        change_given:    changeDue,
        customer_id:     customer?._id ?? null,
        items:           buildItems(),
        note:            note || null,
      });
      finishPayment(payment, { client_uuid });
    } catch (err) {
      setError(err.response?.data?.error ?? 'Error al procesar el pago');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.inner}>
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className={styles.header}>
          <button className="btn btn--ghost" onClick={() => navigate(-1)}>← Volver</button>
          <h1 className={styles.title}>Cobrar</h1>
        </div>

        {/* ── Total ──────────────────────────────────────────────────── */}
        <div className={styles.totalBlock}>
          <p className={styles.totalLabel}>Total a cobrar</p>
          <p className={styles.totalAmount}>{fmt(total)}</p>
          {discount > 0 && (
            <p className={styles.discountLine}>Subtotal {fmt(subtotal)} · Descuento −{fmt(discount)}</p>
          )}
          {customer && (
            <p className={styles.customerChip}><IcoUser size={13} /> {customer.name}</p>
          )}
        </div>

        {/* ── Resumen de artículos ───────────────────────────────────── */}
        <button className={styles.itemsToggle} onClick={() => setShowItems((v) => !v)}>
          <span>{items.length} artículo{items.length !== 1 ? 's' : ''}</span>
          <span className={styles.itemsToggleArrow}>{showItems ? '▲' : '▼'}</span>
        </button>
        {showItems && (
          <div className={styles.itemsList}>
            {items.map((i) => (
              <div key={i.product._id} className={styles.itemsRow}>
                <span className={styles.itemsQty}>{i.quantity}×</span>
                <span className={styles.itemsName}>{i.product.name}</span>
                <span className={styles.itemsTotal}>{fmt(i.total)}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Aviso de stock ─────────────────────────────────────────── */}
        {overStockItems.length > 0 && (
          <div className={styles.stockWarn}>
            <p className={styles.stockWarnTitle}><IcoWarning size={14} /> Stock insuficiente</p>
            {overStockItems.map((i) => (
              <p key={i.product._id} className={styles.stockWarnItem}>
                {i.product.name}: pedido {i.quantity}, disponible {i.product.stock ?? 0}
              </p>
            ))}
            <p className={styles.stockWarnNote}>Puedes continuar si autorizas la venta.</p>
          </div>
        )}

        {/* ── Forma de pago ──────────────────────────────────────────── */}
        <div className={styles.section}>
          <p className={styles.sectionLabel}>Forma de pago</p>
          <div className={styles.methodGrid}>
            {METHODS.map((m) => (
              <button
                key={m.key}
                className={`${styles.methodBtn} ${method === m.key ? styles.methodBtnActive : ''}`}
                onClick={() => setMethod(m.key)}
              >
                <span className={styles.methodIcon}><m.Icon size={20} /></span>
                <span className={styles.methodLabel}>{m.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Pago mixto ─────────────────────────────────────────────── */}
        {method === 'mixed' && (
          <div className={styles.section}>
            <p className={styles.sectionLabel}>Divide el pago</p>
            <div className={styles.currencyRow}>
              <span className={styles.currencySymbol}>$</span>
              <input
                className="input"
                type="number"
                min={0}
                max={total}
                step="0.01"
                placeholder="Monto en efectivo"
                value={mixedCash}
                onChange={(e) => setMixedCash(e.target.value)}
                style={{ paddingLeft: 32, fontSize: 18 }}
                autoFocus
              />
            </div>
            {mixedCash !== '' && (
              <div className={styles.mixedSplit}>
                <div className={styles.mixedRow}>
                  <span><IcoCash size={14} /> Efectivo</span>
                  <strong>{fmt(mixedCashNum)}</strong>
                </div>
                <div className={styles.mixedRow}>
                  <span><IcoCard size={14} /> Tarjeta (lector)</span>
                  <strong>{fmt(mixedCardNum)}</strong>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Estado del lector (card o mixto con tarjeta) ───────────── */}
        {(method === 'card' || method === 'mixed') && (
          <div className={styles.terminalInfo}>
            {terminalPhase === 'waiting' ? (
              <p className={styles.terminalWaiting}>
                {provider === 'mercadopago'
                  ? 'Esperando confirmación en el lector MP...'
                  : 'Acercar tarjeta al lector...'}
              </p>
            ) : terminalPhase === 'preparing' ? (
              <p className={styles.terminalStatus}>Preparando terminal...</p>
            ) : terminalPhase === 'processing' ? (
              <p className={styles.terminalStatus}>Procesando pago...</p>
            ) : provider === 'mercadopago' ? (
              <p className={styles.terminalOk}>Lector Mercado Pago configurado</p>
            ) : terminalStatus === 'connected' ? (
              <p className={styles.terminalOk}>Lector Stripe conectado</p>
            ) : (
              <p className={styles.terminalOff}>Lector no conectado — conéctalo en Configuración</p>
            )}
          </div>
        )}

        {/* ── Efectivo recibido (solo cash) ──────────────────────────── */}
        {method === 'cash' && (
          <div className={styles.section}>
            <p className={styles.sectionLabel}>Efectivo recibido</p>
            <div className={styles.currencyRow}>
              <span className={styles.currencySymbol}>$</span>
              <input
                className="input"
                type="number"
                min={0}
                step="0.01"
                placeholder={total.toFixed(2)}
                value={amountCash}
                onChange={(e) => setAmountCash(e.target.value)}
                style={{ paddingLeft: 32, fontSize: 20 }}
                autoFocus
              />
            </div>
            {/* Denominaciones rápidas */}
            <div className={styles.denominations}>
              {(() => {
                const bills = [20, 50, 100, 200, 500, 1000];
                // Smallest bill that covers total, then the next 3 up
                const minIdx = bills.findIndex((b) => b >= total);
                const start  = minIdx === -1 ? bills.length - 1 : minIdx;
                const shown  = bills.slice(Math.max(0, start - 1), start + 4);
                return shown.map((d) => (
                  <button
                    key={d}
                    className={`${styles.denomBtn} ${Number(amountCash) === d ? styles.denomBtnActive : ''}`}
                    onClick={() => setAmountCash(String(d))}
                  >
                    ${d}
                  </button>
                ));
              })()}
              {/* Botón "exacto" */}
              <button
                className={`${styles.denomBtn} ${Number(amountCash) === total ? styles.denomBtnActive : ''}`}
                onClick={() => setAmountCash(total.toFixed(2))}
              >
                Exacto
              </button>
            </div>
            {/* Cambio a regresar */}
            {amountCash !== '' && (
              changeDue > 0 ? (
                <div className={styles.changeBox}>
                  <span className={styles.changeLabel}>Cambio a regresar</span>
                  <strong className={styles.changeAmount}>{fmt(changeDue)}</strong>
                </div>
              ) : Number(amountCash) < total ? (
                <div className={styles.changeShort}>
                  <span>Faltan</span>
                  <strong>{fmt(total - Number(amountCash))}</strong>
                </div>
              ) : (
                <div className={styles.changeExact}>
                  <span><IcoCheck size={13} /> Monto exacto</span>
                </div>
              )
            )}
          </div>
        )}

        {/* ── Error ──────────────────────────────────────────────────── */}
        {error && <p className={styles.error}>{error}</p>}

        {/* ── Botón cobrar / cancelar ────────────────────────────────── */}
        {loading && terminalPhase ? (
          <button
            className={`btn btn--danger btn--full ${styles.chargeBtn}`}
            onClick={() => abortRef.current?.abort()}
          >
            Cancelar cobro
          </button>
        ) : (
          <button
            className={`btn btn--primary btn--full ${styles.chargeBtn}`}
            onClick={handleCharge}
            disabled={
              loading ||
              items.length === 0 ||
              (method === 'cash' && amountCash !== '' && Number(amountCash) < total) ||
              (method === 'mixed' && !mixedValid)
            }
          >
            {loading ? <span className="spinner" /> : `Cobrar ${fmt(total)}`}
          </button>
        )}
      </div>
    </div>
  );
}
